import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, IconButton, MenuItem, Paper, Select, Slider, Stack, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { createChart } from 'lightweight-charts';

import { useAuth } from '../context/AuthContext';
import { streamSimulation } from '../utils/dynamanSimulation';
import {
  appendFeed, barLine, FEED_COLORS, intentLine, rejectionLine, toCandle, transitionLine,
} from '../utils/dynamanFeed';
import DynaManStatsPanel from './DynaManStatsPanel';
import DynaManLedgerTable from './DynaManLedgerTable';

/**
 * THE LIVE SIMULATOR: watch the FSM decide, bar by bar, on a chart.
 *
 * <h2>lightweight-charts, not ECharts</h2>
 *
 * ECharts drives every other chart here, and for a STREAM it is the wrong tool: appending a bar
 * means handing `setOption` a growing array and letting it diff, once a second, for up to two
 * thousand bars. `lightweight-charts` — already a dependency at 4.1.3 and, until now, unused —
 * has `series.update(bar)`, which is the exact primitive this needs and is O(1). The chart is
 * behind {@link StreamingChart} so swapping it back is a one-file change.
 *
 * <h2>The account numbers come from the server, never from arithmetic here</h2>
 *
 * Every bar event carries the account state, computed by the same `AccountSimulation` call that
 * produces the final report. The temptation is to accumulate equity in the browser as transitions
 * arrive — and that is a second implementation of sizing, slippage and commission, which is how a
 * HUD comes to disagree with the summary it has spent two minutes previewing.
 *
 * <h2>Refs for the chart, state for React</h2>
 *
 * A candle is pushed into an imperative chart handle via a ref, so a bar does not re-render the
 * tree; the feed and the account panel are state, because they are what React draws. Putting the
 * candle array in state as well would re-render the whole page at every bar for a chart that had
 * already drawn itself.
 */

const DELAY_MARKS = [
  { value: 500, label: '0.5s' },
  { value: 1000, label: '1s' },
  { value: 2000, label: '2s' },
];

/** The demo's account. Shown in the UI, so a viewer knows the numbers are net rather than gross. */
export const DEMO_ACCOUNT = {
  startingEquity: 100000,
  riskPerTradePct: 1.0,
  commissionPerShare: 0.005,
  slippageBps: 5,
};

/**
 * The account every DEBUGGER run carries: one million, 1% risk a trade, real costs.
 *
 * <p>Sized rather than frictionless because "Unsized run — percentages only" is not a reading. A
 * percentage ledger cannot say max drawdown in dollars, cannot separate gross gain from gross
 * loss, and cannot answer "what would this have done to the book" — which is the question being
 * asked while walking a trade.
 *
 * <p>A DUMMY account, and nothing reads it as a position size: it exists so the HUD has a
 * currency to report in. The costs are the same 0.005/share and 5bps every published net figure
 * was measured with, so a number here is comparable with the research rather than a cleaner
 * version of it.
 */
export const DEBUG_ACCOUNT = {
  startingEquity: 1000000,
  riskPerTradePct: 1.0,
  commissionPerShare: 0.005,
  slippageBps: 5,
};

/**
 * The series a REPLAY loads: the signal timeframe alone.
 *
 * <p>One frame per chart candle, which is the only arrangement in which Step can move both the
 * chart and the HUD. With every stored series loaded, most frames on SPY are 5-minute bars and
 * the daily playhead does not move for dozens of clicks.
 */
export const REPLAY_TIMEFRAMES = ['daily'];

/**
 * The strategy every run in this app uses, minus the provider — which is the viewer's to pick.
 *
 * <h2>RATCHETING_GATES, and this is a deliberate change of rules</h2>
 *
 * It was `DYNAMIC_TRENDLINE_BREAK`, which is a single close-basis exit over a stop that never
 * moved. Under it a premise whose exit was unreachable had no live protection but a stale level —
 * on SPY the replay drew one trade as a straight line from 1995 to 2020 for exactly that reason.
 *
 * <p>`RATCHETING_GATES` composes three gates onto one resting stop: the breakeven at 1R, the
 * excursion trail at 2R, and the swing trendline as it re-anchors. Measured on SPY daily, same
 * stop and same data:
 *
 * <pre>
 *   ATR_TRAILING_FLOOR       2 trades   -2.38%   PF 0.00   DD  2.38%   still holding
 *   DYNAMIC_TRENDLINE_BREAK  120        +19.58%  PF 1.20   DD 28.45%   still holding
 *   RATCHETING_GATES         135        +34.17%  PF 1.40   DD 13.05%   flat
 * </pre>
 *
 * <p><b>The published provider figures below predate it.</b> They were measured under the
 * trendline exit, so they describe a different exit surface from the one this app now runs — the
 * ledger on screen is the authority for any given run, not the hint text.
 */
export const DEMO_STRATEGY = {
  exitStrategy: 'RATCHETING_GATES',
  stopStrategy: 'SIGNAL_BAR_EXTREME',
  // A QUARTER ATR. The sweep (0.25 / 0.5 / 0.75 / 1.0 / 1.5) found gross PnL falling monotonically
  // as the buffer widens and 0.25 the only net-profitable setting — see DynaManRuleConfig.
  // Sent EXPLICITLY, as all three are: a value sent here OVERRIDES the backend default, so leaving
  // a stale number would silently keep the app on different physics from every CLI figure.
  stopAtrBuffer: 0.25,
  // ISOLATE THE SELECTED PROVIDER, which is what the provider dropdown reads as meaning.
  //
  // Without this the engine ran the chosen provider AND the Donchian breakout together, and
  // CLAUDE.md's own note on the pair applies: "a run with both attached is not a run of either".
  // Every published per-provider figure was measured under --disable-structural-entry, so the
  // on-screen ledger disagreed with every number anyone would compare it against — the same class
  // of defect as the dialog once sending `{ symbol }` alone and silently running a different exit.
  // NO LONGER HERE. Provider isolation is a SWITCH the dialog owns, because a value hardcoded in
  // every request is a value nobody can see or test -- and "is my provider running alongside the
  // Donchian breakout" is a legitimate question about any run. The dialog sends it on every path
  // and defaults it ON, which is the behaviour every per-provider figure was measured under.
  //
  // The live view keeps it, because it has no controls of its own and must not silently run a
  // different strategy from the dialog a click away -- the defect this file has already shipped.
  disableStructuralEntry: true,

  // NO ENGINE PRESET, SENT EXPLICITLY, and this is the one value here that had to be added rather
  // than merely recorded.
  //
  // The API DEFAULTS the preset to on -- a request that says nothing about the engine's environment
  // came from the dialog, which always selects one. The research dialog, the live view and the
  // three one-click engine icons are the callers that say nothing, and they exist to reproduce the
  // LOCKED parameters above: a preset would turn the fan on, the macro gate on and the book up to
  // three tranches, so every figure this view is checked against would move without a single
  // visible control having changed. `false` is the only value the wire can use to say that.
  enginePreset: false,
};

/**
 * TRENDPULSE'S LEVERS — shape detection, not moving averages.
 *
 * <h2>What is actually tunable here</h2>
 *
 * TrendPulse does not smooth price. It reads a PUBLISHED momentum series (the precomputed
 * TrendPulse dataset) and looks for shapes in it: twin peaks (M), twin troughs (W), capitulation
 * drops and exhaustion spikes. So the levers are about how a peak is RECOGNISED — how wide, how
 * prominent, how long an extreme must run — and there is no fast/slow average to set. Those
 * belong to the structural breakout, which is a different entry path and is disabled in a
 * standalone profile.
 *
 * <h2>Thresholds are in units of the pulse's OWN average move</h2>
 *
 * The published series runs 4,618 to 18,080 with a median near 9,700, so a threshold in POINTS
 * would be routine in one decade and unheard of in another. Prominence is therefore measured in
 * average-moves, not points.
 *
 * <p>The defaults are an ADMISSION, not a derivation: they were chosen to make the shapes
 * selective (five or six a year each) and nothing has shown them to be right. There is no ground
 * truth for "a correct double top", which is exactly why they are exposed.
 */
export const TRENDPULSE_LEVERS = [
  {
    key: 'tpPeakWidth',
    label: 'Peak width',
    min: 1,
    max: 8,
    step: 1,
    default: 3,
    hint: 'Half-width of an RSI-style fractal peak in the pulse. A peak is confirmed this many '
      + 'bars LATE, because its right side has to print first. At 1 the M/W detectors fire far '
      + 'more often and far noisier.',
  },
  {
    key: 'tpProminence',
    label: 'Peak prominence',
    min: 0.5,
    max: 10,
    step: 0.5,
    default: 4.0,
    hint: 'How far a twin peak must stand clear of the trough between them, in average-moves. '
      + 'Before this existed the twin-shape detector matched NOTHING in 33 years.',
  },
  {
    key: 'tpExtremeDuration',
    label: 'Extreme duration',
    min: 2,
    max: 20,
    step: 1,
    default: 5,
    hint: 'Bars a capitulation or exhaustion slope must run. Lowering it 8 -> 5 took extreme '
      + 'setups from 9 to 42 over the SPY history, which is what made them a sample.',
  },
];

/**
 * THE CTS DIRECTION RULES, and what each one measured.
 *
 * <h2>The hints are the findings, not marketing</h2>
 *
 * Every one of these is weak — no classifier measured here beats a coin flip on the full signal
 * set — so the dropdown exists for the visual audit rather than as a menu of improvements. A
 * reader switching to RSI_DIVERGENCE and watching the result get worse should be able to see on
 * the same screen that this is the expected outcome.
 *
 * <p>Figures are daily SPY, orb 1.0, the shipped exit surface with PTA targets on.
 */
export const CTS_DIRECTION_RULES = [
  {
    value: 'SMA_PULLBACK',
    label: 'SMA pullback (default)',
    hint: 'Close vs the 5-bar mean. +53.50% gross, PF 1.37 — the best measured, and still only '
      + '42.9% correct on moves above 7%.',
  },
  {
    value: 'RSI_DIVERGENCE',
    label: 'RSI divergence',
    hint: 'Price/RSI divergence, then an exhaustion ladder, then RSI vs 50. +13.50%, PF 1.12 — '
      + 'worse at all 7 parameterisations swept.',
  },
  {
    value: 'SWING_EXTREME',
    label: 'Swing extreme (10-bar)',
    hint: 'Fades a bar that prints the window extreme. Only 110 trades, but the best mean R (0.42) '
      + 'and lowest drawdown (14.17%).',
  },
  {
    value: 'WICK_ANATOMY',
    label: 'Wick anatomy',
    hint: 'Fades the dominant rejection wick. The ONLY rule that reads COVID and Lehman correctly '
      + '— and the worst overall, PF 0.94.',
  },
  {
    value: 'T1_CONTEXT',
    label: 'T-1 context (20-bar)',
    hint: 'Ignores the signal bar entirely, reading T-1 against a 20-bar mean. +35.80%, PF 1.25.',
  },
  {
    value: 'INFLECTION_VECTOR',
    label: 'Inflection vector ("look left")',
    hint: 'Reads the TRAVEL into the node, not price’s position against a mean: rallied in is a '
      + 'top, dropped in is a bottom. +66.82% gross at PF 1.37 on the same 238 trades — and '
      + '+78.09% at PF 1.41 with the entry gate below and the node-anchored stop. It does NOT '
      + 'fix the crash-onset error: '
      + 'a violent down bar is a DROP into the node, so this buys it too.',
  },
];

/** What the engine itself defaults to. Reverted to SMA_PULLBACK on evidence. */
export const DEFAULT_CTS_DIRECTION = 'SMA_PULLBACK';

/**
 * THE CTS ENTRY GATE: the node ARMS a setup, a later session's price TRIGGERS it.
 *
 * <p>Same schema as {@link CTS_EXIT_LEVERS}, so the dialog renders all three lever blocks from
 * one component and a lever added here needs no second edit.
 *
 * <h2>The gate DEFAULTS ON here and OFF in the engine, deliberately</h2>
 *
 * `DynaManRuleConfig` ships it false so the locked 239-line CTS ledger and every figure recorded
 * against it still reproduce from a bare CLI command. This dialog turns it on, because it is the
 * better-measured configuration and because a research surface should open on the thing worth
 * looking at. The consequence is stated rather than hidden: **a CTS run launched from this dialog
 * no longer reproduces the locked baseline** — untick the gate for that.
 */
export const CTS_ENTRY_LEVERS = [
  { key: 'ctsPriceConfirmation', type: 'boolean', default: true,
    label: 'Price confirmation entry gate',
    hint: 'The node ARMS a setup instead of entering it: nothing is taken until a later session '
      + 'breaks the node bar’s extreme in the called direction. A node whose window runs out, or '
      + 'which a newer node supersedes, is counted as BYPASSED with zero execution — the number '
      + 'the gate is judged on, shown in the run summary. Measured on SPY: 261 armed, 176 '
      + 'executed, 85 bypassed; mean R 0.21 → 0.50. ON by default here, OFF in the engine.' },
  { key: 'ctsConfirmationWindow', type: 'number', default: 5, step: 1, min: 1,
    enabledBy: 'ctsPriceConfirmation',
    label: 'Confirmation window (bars)',
    hint: 'How long a node stays armed. Matched to the cooldown + 1, so it is exactly the stretch '
      + 'during which this node is the only one that could be confirmed. Swept 1/3/5/10/20 and 5 '
      + 'measured best, but it was chosen a priori — the sweep agreeing is weaker than it looks.' },
  { key: 'ctsConfirmOnClose', type: 'boolean', default: false,
    enabledBy: 'ctsPriceConfirmation',
    label: 'Confirm on the CLOSE (not the touch)',
    hint: 'Off, a break is a TOUCH — which is what a resting stop-entry order actually is, filled '
      + 'at the level or at the open on a gap. On, the bar must FINISH beyond the extreme and '
      + 'fills at that close: later, stricter, and measured BETTER here (117 trades, +73.18% at '
      + 'PF 1.53 against +20.55% at 1.17). Not the default only because a touch is what an order '
      + 'does, and that is one cell of one sweep.' },
  { key: 'ctsVectorLookback', type: 'number', default: 3, step: 1, min: 1,
    label: 'Inflection vector lookback (bars)',
    hint: 'Bars the "look left" rule measures travel across. Read only when the direction rule '
      + 'above is INFLECTION_VECTOR. Short on purpose: the question is what price did on the '
      + 'APPROACH, and a long window turns it into a trend filter that fades every trend.' },
  { key: 'stopPointOffset', type: 'number', default: 2, step: 0.5, min: 0,
    label: 'Wick-stop offset (POINTS)',
    hint: 'Read only when the stop strategy is NODE_WICK_OFFSET: the stop sits this many points '
      + 'past the NODE bar’s extreme wick, never the bar that filled. THE ONLY STOP LEVER HERE '
      + 'THAT IS NOT SCALE-FREE — two points is 4.5% of SPY in 1993 and 0.33% in 2026, so on this '
      + 'symbol it measures badly (R:R 6.17 → 2.60, and every offset 0–10 loses). It belongs on a '
      + 'fixed-size index future.' },
];

/**
 * The signal providers a viewer can choose between.
 *
 * <p>Only the three that raise intents. `BOTH` and `NONE` exist on the wire and in the CLI but
 * are not offered here: NONE draws an empty feed, which looks like a broken demo, and BOTH puts
 * two independent sources in one engine so the result is a measurement of neither.
 *
 * <p>The descriptions are the measured findings, not marketing. A prospect watching CTS produce
 * a net loss should be able to see, on the same screen, that this is the expected result.
 *
 * <p><b>These figures come from the DYNAMIC_TRENDLINE_BREAK study</b>, which is no longer the
 * exit this app runs — see {@link DEMO_STRATEGY}. They are kept because they are still the only
 * measured net-of-cost numbers per provider, and labelled because a hint that silently described
 * a different configuration would be worse than no hint.
 */
/**
 * PROVIDERS THAT BRING THEIR OWN EXIT, and what they bring.
 *
 * <h2>Why PTA and PTB must not inherit the CTS exit surface</h2>
 *
 * {@code RATCHETING_GATES} is the engine default and it was tuned for CTS: a breakeven gate at 1R,
 * an excursion trail at 3R, and a 0.25 ATR initial stop arrived at by sweeping CTS returns. None of
 * that belongs to the PTA/PTB ruleset, whose stated exit is a breach of the fractal-pivot
 * trendline. Running PTA on the gates would make the result a measurement of CTS's exit machinery
 * applied to PTA's entries -- a number describing neither system.
 *
 * <p>Declared here as data rather than written into each call site, because the dialog has four
 * run paths and a provider that picked up the right exit on three of them would be the worst
 * possible outcome: three views agreeing and one quietly different.
 */
export const PROVIDER_EXIT_OVERRIDES = {
  PTA: 'DYNAMIC_TRENDLINE_BREAK',
  PTB: 'DYNAMIC_TRENDLINE_BREAK',
};

/**
 * THE LOCKED BAND BASELINE — the configuration that took PTA to PF 1.00 and PTB to net +41.44%.
 *
 * <h2>Sent explicitly, and the reason is a near miss</h2>
 *
 * {@link DEMO_STRATEGY} carries `stopAtrBuffer: 0.25`, which is the CTS figure, swept on CTS
 * returns. For a band run the backend's `SignalProviderProfile` overrides it to 0.5 — so the app
 * was on the right physics by the grace of the controller not marking the buffer explicit, while
 * a reader of this file would have concluded the UI ran 0.25.
 *
 * <p>That is the dialog-disagrees-with-the-CLI defect this repository has shipped twice. So the
 * band values are stated HERE rather than left to be overridden elsewhere: the request says what
 * it means, and `DynaManSimulatorDialog.config.test.js` pins all five against the Java profile.
 *
 * <h2>What each one is, and what it cost to find</h2>
 *
 * <pre>
 *   trendlineFractal 5    an eleven-bar fractal. At 3 a close SIXTEEN CENTS through a line ended
 *                         a 29-bar winner on 1996-10-24 and the entry rules then raised nothing
 *                         for three months. Worth +13 points of gross on PTA, +17.8 on PTB
 *   stopAtrBuffer 0.5     the band stop. 1.0 flatters PTA and takes 92 points off PTB
 *   conformingBarTrailAtr 2.0   a catastrophe parachute, NOT the specified 0.1 profit lock, which
 *                         capped every winner at about a percent
 *   bandReentryBars 5     the buy-back. +5.5 points on PTA, +36.9 on PTB, drawdown DOWN on both
 *   phaseManagement false it measured worse on both systems: phases 1-2 silence the parachute
 * </pre>
 */
export const BAND_BASELINE = {
  trendlineFractal: 5,
  stopAtrBuffer: 0.5,
  conformingBarTrailAtr: 2.0,
  bandReentryBars: 5,
  phaseManagement: false,
};

/**
 * THE CONTAINMENT FIELD'S OWN STOP, for PTA only.
 *
 * <h2>Behaviourally a no-op, and that is the point</h2>
 *
 * The backend profile already forces these for a PTA run, and the controller does not mark
 * {@code stopStrategy} or {@code stopAtrUnits} as explicitly set — so the profile wins either way
 * and sending them changes nothing about the result.
 *
 * <p>What it changes is whether the REQUEST is honest. Without this the dialog sends
 * {@code stopStrategy: 'SIGNAL_BAR_EXTREME'} from {@link DEMO_STRATEGY} while the backend quietly
 * runs {@code FIXED_ATR} at 1.0 ATR, and a reader of this file would conclude the UI runs the
 * former. That is the identical invisible divergence the CTS-tuned `stopAtrBuffer: 0.25` created,
 * and it is the defect class this dialog has shipped twice.
 *
 * <h2>PTA only, not every band provider</h2>
 *
 * PTB still trades the eight micro-crossing rules on a SIGNAL_BAR_EXTREME stop with a 0.5 ATR
 * buffer. Putting FIXED_ATR in {@link BAND_BASELINE} would misdescribe PTB — harmlessly, since the
 * profile decides, but a request that misdescribes the run is the whole thing being fixed here.
 */
export const CONTAINMENT_BASELINE = {
  stopStrategy: 'FIXED_ATR',
  stopAtrUnits: 1.0,
};

/** True for the time-measurement providers, which carry their own exit surface. */
export const isBandProvider = (provider) => provider === 'PTA' || provider === 'PTB';

/** True for the provider on the containment field. PTA alone; PTB keeps the eight rules. */
export const isContainmentProvider = (provider) => provider === 'PTA';

/**
 * The strategy for a run: shared defaults, then the provider's exit, then the band baseline.
 *
 * <p>Order is load-bearing. {@link BAND_BASELINE} goes LAST so its `stopAtrBuffer` replaces
 * {@link DEMO_STRATEGY}'s CTS-tuned 0.25 rather than the other way round.
 */
export function strategyFor(provider) {
  const exitStrategy = PROVIDER_EXIT_OVERRIDES[provider];
  const base = exitStrategy ? { ...DEMO_STRATEGY, exitStrategy } : { ...DEMO_STRATEGY };
  if (!isBandProvider(provider)) return base;
  const band = { ...base, ...BAND_BASELINE };
  // ORDER IS LOAD-BEARING, again. The containment stop goes LAST so FIXED_ATR replaces
  // DEMO_STRATEGY's SIGNAL_BAR_EXTREME rather than the other way round.
  return isContainmentProvider(provider) ? { ...band, ...CONTAINMENT_BASELINE } : band;
}

/**
 * THE CTS EXIT SANDBOX's levers: ONE declaration, read by the dialog for everything.
 *
 * <p>The list IS the wiring. Each entry supplies the API parameter name, the control type, the
 * label, the engine default and the sentence under it — so a lever added here appears in the
 * panel, reaches all four run paths and defaults correctly, with no matching line anywhere else.
 * Sixteen separate `useState` calls would be sixteen chances to wire fifteen, and the one that
 * was missed would look like a rule that does nothing.
 *
 * <p>Every default is the ENGINE's default, so an untouched panel sends what the backend would
 * have used anyway and the locked CTS baseline reproduces from a run nobody configured.
 *
 * <p>CTS ONLY. `SignalProviderProfile` forces `ctsExitSandbox` false for PTA and PTB, which are
 * code-frozen, so none of these can reach a band run however they are set.
 */
/**
 * THE MEASURED BASELINE FOR EACH PROVIDER, applied when the dropdown changes.
 *
 * <h2>Why the dialog should not open on a configuration nobody recommends</h2>
 *
 * Switching engine used to leave every toggle where the previous one had it, so a reader moving
 * from CTS to PTA ran PTA with CTS's settings and compared the result against figures measured
 * under neither. The defaults below are each the best configuration MEASURED for that provider,
 * and they are applied on every change of the dropdown.
 *
 * <pre>
 *   PTA   macro gate ON   185 ->  142 trades, +28.65% -> +71.65% gross, PF 1.18 -> 1.48
 *   PTB   macro gate ON   314 ->  252 trades, +54.52% -> +197.66% gross, PF 1.19 -> 1.49
 *   CTS   macro gate OFF  the 50/200 pair is a TREND filter and CTS is a mean-reversion
 *                         exhaustion study, so the gate removes the trades the study is FOR:
 *                         238 -> 88 trades, +41.42% -> +11.47%, net +1.41% -> -6.16%
 *   CTS   time ejector 5  254 trades, +91.17% gross, PF 1.39, net +22.87% -- the only sandbox
 *                         mechanic measured better than doing nothing
 * </pre>
 *
 * <h2>Two things this deliberately does</h2>
 *
 * It OVERWRITES a manual edit when the provider changes. A reader who tuned a lever, switched
 * engine and switched back would otherwise carry a setting across a boundary where it was never
 * measured — which is the defect being fixed, in a subtler form.
 *
 * <p>And CTS no longer opens on its locked 238-trade baseline: the time ejector is on, which
 * turns the exit sandbox on with it. That baseline is one toggle away (Time ejector to 0) and
 * the CLI reproduces it from a bare command, which is where reproducibility belongs. The dialog
 * is for reading an engine at its best, not for reproducing a record.
 */
export const PROVIDER_BASELINES = {
  PTA: {
    macroGate: true,
    ctsExit: { ctsMaxHoldBars: 0, ctsTimeEjectorEnabled: false,
      ctsExitSandbox: false },
  },
  PTB: {
    macroGate: true,
    ctsExit: { ctsMaxHoldBars: 0, ctsTimeEjectorEnabled: false,
      ctsExitSandbox: false },
  },
  // The gate OFF and the ejector at its measured optimum. Both are CTS-specific: the band
  // systems force the sandbox off, so those entries above are housekeeping rather than rules.
  CTS: {
    macroGate: false,
    ctsExit: { ctsMaxHoldBars: 5, ctsTimeEjectorEnabled: true,
      ctsExitSandbox: true },
  },
};

/** The baseline for a provider, or null when it has no measured one. */
export const baselineFor = (provider) => PROVIDER_BASELINES[provider] || null;

/**
 * THE SUB-MECHANICS THAT IMPLY THE MASTER SWITCH.
 *
 * <p>The helper text has always said the master is "implied by any switch below", and from this
 * app it was not true. The CLI implies it -- each mechanic flag sets `ctsExitSandbox` as it is
 * parsed -- but the dialog sends the master EXPLICITLY on every request, and the backend honours
 * an explicit value over its own implication. So turning MACD on with the master visually off
 * sent `ctsExitSandbox=false`, and the engine ignored MACD entirely: a run that looked
 * configured and was not, reporting the default sandbox result as if it were the crossover's.
 *
 * <p>Used in TWO places, deliberately -- the state effect that forces the switch ON so the panel
 * cannot lie, and the payload builder that recomputes it so the request cannot either. A reader
 * who finds one without the other should find a reason rather than an omission.
 */
export const CTS_SANDBOX_IMPLIED_BY = [
  'ctsSignalToSignal', 'ctsRsiExit', 'ctsMacdExit', 'ctsTrendlineExit',
  'ctsConsecutiveReentry', 'ctsTimeEjectorEnabled',
];

/**
 * Whether these lever values require the sandbox, whatever the master switch says.
 *
 * <p>`ctsBreathableStopEnabled === false` USED to imply it as well, on the reasoning that
 * deviating from the sandbox's own stop is something only the sandbox can express. That was
 * wrong twice over: the flag is read only when the sandbox already governs, so off-implies-on
 * was circular, and it made "every child off" an unreachable state -- which is half of why the
 * master could not be turned off. It is not an implier.
 */
export const sandboxImplied = (levers) =>
  CTS_SANDBOX_IMPLIED_BY.some((k) => !!levers[k]);

/**
 * EVERY CHILD MECHANIC IN ITS OFF STATE.
 *
 * <p>What the panel is set to, and what the payload sends, when the master is switched off. The
 * master must be genuinely optional: `RATCHETING_GATES` has its own stop, breakeven and trail,
 * and it is the configuration the profitable SPY baseline was measured under. An exit manager
 * that could not be declined would have made that baseline unreachable from the UI.
 */
export const CTS_MECHANICS_OFF = {
  ctsSignalToSignal: false,
  ctsRsiExit: false,
  ctsMacdExit: false,
  ctsTrendlineExit: false,
  ctsConsecutiveReentry: false,
  ctsTimeEjectorEnabled: false,
  ctsBreathableStopEnabled: false,
  // The ENGINE reads bars, not the switch above it. Left at 5 this keeps ejecting trades after
  // the master is off -- which is exactly the silent override being fixed.
  ctsMaxHoldBars: 0,
};

/**
 * PROFIT PROTECTION — the Exit Parameters panel.
 *
 * <p>Same schema as {@link CTS_EXIT_LEVERS}, so the dialog renders both from one component and a
 * lever added here needs no matching line anywhere else. Every default is the ENGINE's default,
 * which means an untouched dialog sends exactly what the backend would have used and every
 * published figure reproduces from a run nobody configured.
 *
 * <p>BOTH FEATURES ARE OFF. That is not a preference: the regression guard asserts that with them
 * off the ledger, the PnL and the max drawdown are unchanged, and it was verified on real data by
 * diffing the full CLI ledger for CTS, PTA and PTB either side of the change.
 *
 * <p>Unlike the CTS panel, these are shown for EVERY provider. They are stop-management rules
 * rather than one engine's own mechanics, and they are polled on all three exit surfaces —
 * including {@code ATR_TRAILING_FLOOR}, which is the engine default and originally polled
 * neither manager, so the switch silently did nothing there.
 */
export const EXIT_PROTECTION_LEVERS = [
  { key: 'breakevenLockEnabled', type: 'boolean', default: false,
    label: 'Enable breakeven lock',
    hint: 'Once the trade is far enough in front, move the stop to the entry plus a sliver so it '
      + 'can no longer become a loss. Distinct from the R-based breakeven gate: this one arms on '
      + 'a percentage of PRICE, so on a tight-stopped instrument the two fire at very different '
      + 'times.' },
  { key: 'breakevenLockActivationPct', type: 'number', default: 1.5, step: 0.1, min: 0,
    label: 'Lock activation %', enabledBy: 'breakevenLockEnabled',
    hint: 'Unrealized profit required to arm the lock, measured on the bar CLOSE against the '
      + 'entry. Measuring it from the favourable extreme would arm on a wick the position never '
      + 'closed above.' },
  { key: 'breakevenLockOffsetPct', type: 'number', default: 0.05, step: 0.01, min: 0,
    label: 'Lock offset %', enabledBy: 'breakevenLockEnabled',
    hint: 'Where the stop sits relative to entry once locked, IN THE TRADE’S FAVOUR — above for '
      + 'a long, below for a short. Zero is exact breakeven. Negative is refused: it would lock '
      + 'in a loss under a name that promises not to.' },
  { key: 'acceleratedRatchetEnabled', type: 'boolean', default: false,
    label: 'Enable accelerated ratchet',
    hint: 'Past a threshold, tighten the trail so it hugs the favourable extreme instead of '
      + 'giving the trade its full initial room.' },
  { key: 'ratchetActivationPct', type: 'number', default: 2.5, step: 0.1, min: 0,
    label: 'Ratchet activation %', enabledBy: 'acceleratedRatchetEnabled',
    hint: 'Unrealized profit required to begin accelerating, on the bar close.' },
  { key: 'ratchetTighteningPct', type: 'number', default: 50, step: 5, min: 0,
    label: 'Ratchet tightening %', enabledBy: 'acceleratedRatchetEnabled',
    hint: 'How much of the trail distance to remove. The distance is the trade’s INITIAL RISK, '
      + 'which is fixed at entry — reading it as "the current gap to the stop" would compound on '
      + 'every bar and converge on the price within a handful of them, stopping every armed '
      + 'trade out. 100 is refused: the stop would sit on the extreme itself.' },
];

/**
 * The engines a COMPOSITE run may include. All three on by default.
 *
 * <p>Each runs its own state machine over the same bars and holds its own single premise, so
 * their positions genuinely overlap — a CTS long and a PTB long at once — and the merged ledger
 * tags every trade with the engine that took it.
 */
export const COMPOSITE_ENGINES = [
  { key: 'PTA', label: 'Include PTA' },
  { key: 'PTB', label: 'Include PTB' },
  { key: 'CTS', label: 'Include CTS' },
];

export const CTS_EXIT_LEVERS = [
  { key: 'ctsExitSandbox', type: 'boolean', default: false,
    label: 'Use the CTS exit manager',
    hint: 'Replaces the band systems’ ratcheting gates. Implied by any switch below, so '
      + 'turning one on never silently does nothing.' },
  { key: 'ctsSignalToSignal', type: 'boolean', default: false,
    label: 'Pure signal-to-signal',
    hint: 'Hold until the opposing CTS signal: no stop, no trail, no target, no time limit. '
      + 'Overrides everything below it — a mode that was partly in force would not be the '
      + 'mode it says.' },
  { key: 'ctsBreathableStopEnabled', type: 'boolean', default: true,
    label: 'Breathable stop',
    hint: 'On, the sandbox prices its own entry stop. Off, the configured stop strategy applies '
      + 'instead — which on the current defaults is TIGHTER, not looser: measured on SPY, MACD '
      + 'exits fall 114 to 56 because more trades are stopped out first. To give a slow rule '
      + 'room, WIDEN the ATR below rather than switching this off.' },
  { key: 'ctsInitialStopAtr', type: 'number', default: 1.5, step: 0.25,
    enabledBy: 'ctsBreathableStopEnabled',
    label: 'Breathable stop (ATR)',
    hint: 'A temporal pivot is a claim about WHEN, so the entry bar’s extreme is not a level '
      + 'the market agreed to defend. A tight stop there measures ambient noise.' },
  { key: 'ctsTimeEjectorEnabled', type: 'boolean', default: false,
    label: 'Time ejector',
    hint: 'A hard limit on how long "now" lasts. Switch it OFF to let a slow indicator run — it '
      + 'outranks every oscillator, so it can eject a trade the rule under test never got to '
      + 'judge.' },
  { key: 'ctsMaxHoldBars', type: 'number', default: 5, step: 1,
    enabledBy: 'ctsTimeEjectorEnabled',
    label: 'Time ejector (bars)',
    hint: 'Exit at the close after this many bars, whatever the position is doing.' },
  { key: 'ctsRsiExit', type: 'boolean', default: false,
    label: 'RSI exit',
    hint: 'A TAKE-PROFIT, not a stop: a long exits at overbought because that IS the snap-back '
      + 'having happened. Needs a crossing, so an entry taken into an extreme is not ejected on '
      + 'its first bar.' },
  { key: 'ctsRsiPeriod', type: 'number', default: 14, step: 1, label: 'RSI period',
    hint: 'Lookback for the oscillator.' },
  { key: 'ctsRsiOverbought', type: 'number', default: 70, step: 5, label: 'RSI overbought (long exit)',
    hint: 'Where a long takes its profit.' },
  { key: 'ctsRsiOversold', type: 'number', default: 30, step: 5, label: 'RSI oversold (short exit)',
    hint: 'Where a short takes its profit.' },
  { key: 'ctsMacdExit', type: 'boolean', default: false,
    label: 'MACD exit',
    hint: 'Exit on a signal-line crossover AGAINST the trade — momentum turning over.' },
  { key: 'ctsMacdFast', type: 'number', default: 12, step: 1, label: 'MACD fast', hint: '' },
  { key: 'ctsMacdSlow', type: 'number', default: 26, step: 1, label: 'MACD slow', hint: '' },
  { key: 'ctsMacdSignal', type: 'number', default: 9, step: 1, label: 'MACD signal', hint: '' },
  { key: 'ctsTrendlineExit', type: 'boolean', default: false,
    label: 'Structural trendline exit',
    hint: 'The same swing geometry the band systems use, fitted to pivots since entry.' },
  { key: 'ctsTrendlineAtrBuffer', type: 'number', default: 1.0, step: 0.25,
    label: 'Trendline margin (ATR)',
    hint: 'SEPARATE from the band buffer on purpose: an inflection trade sits closer to its own '
      + 'structure, so the band margin chops it out for a reason unrelated to the signal. Widen '
      + 'it to survive chop.' },
  { key: 'ctsTrendlineFractalWidth', type: 'number', default: 3, step: 1,
    label: 'Trendline fractal width', hint: 'Half-width of the pivots the line is fitted through.' },
  { key: 'ctsConsecutiveReentry', type: 'boolean', default: false,
    label: 'Permit consecutive re-entry',
    hint: 'Take a CTS signal that arrives on the bar a premise is stopped out, instead of '
      + 'discarding it. Does NOT relax the provider’s 4-bar cooldown — a signal folded '
      + 'away there never becomes an intent at all.' },
];

export const SIGNAL_PROVIDERS = [
  {
    value: 'TRENDPULSE',
    label: 'TrendPulse',
    hint: 'Standalone on SPY: 117 trades, 27.4% win, +43.42% gross, PF 1.80, max DD 10.55%. '
      + 'The best profit factor and the lowest drawdown of the three, on half the trade count.',
  },
  {
    value: 'CTS',
    label: 'CTS (temporal pivots)',
    hint: 'Standalone on SPY: 250 trades, 29.6% win, +61.40% gross, PF 1.44, max DD 13.81%. '
      + 'More return from more trades, at a lower profit factor.',
  },
  {
    value: 'PTA',
    label: 'PTA (time measurement)',
    hint: 'THE EQUATOR, BOTH SIDES. The primary trigger is a close crossing the MIDLINE halfway '
      + 'between the Top and Bottom lines (A-EQU-B / A-EQU-S); the perimeter is a fallback for a '
      + 'runaway gap, live only while that cycle has not touched the midline (A-PER-B / A-PER-S). '
      + 'There is no no-trade zone -- the equator is inside the box. Daily SPY: 185 trades, 28.6% '
      + 'win, +28.65% gross, PF 1.18, max DD 23.11%, net +10.17%. THE SIDES DIVERGE SHARPLY: long '
      + '93 trades at PF 2.11 (+120.39%), short 92 at PF 0.56 (-41.63%), and A-EQU-B alone is 60 '
      + 'trades at PF 2.55 with every decade positive. The forward-return test scores the entries '
      + 'NEUTRAL rather than anti-predictive (A-EQU-B t -0.34 / -1.29 / -0.03), which is better '
      + 'than the perimeter vocabulary managed -- but neutral is not an edge, so the long side\'s '
      + 'profit is still the exit machinery working with SPY\'s upward drift.',
  },
  {
    value: 'PTB',
    label: 'PTB (time measurement)',
    hint: 'The same eight rules on PTB\'s own band timeline, codes BRUT-B / BPUT-B and so on. '
      + 'Daily SPY: 320 trades, 28.8% win, -0.19% gross, PF 1.05, net -0.41%. Also shows no '
      + 'directional edge in the forward-return test.',
  },
  {
    value: 'CONFLUENCE',
    label: 'Confluence (TrendPulse near a CTS date)',
    hint: 'Standalone on SPY: 20 trades, 30.0% win, +14.54% gross, PF 1.83 — but the lift of CTS '
      + 'dates on TrendPulse shapes measured 0.88-1.18x, i.e. NONE. It samples TrendPulse at '
      + 'random rather than selecting from it, so the PF is a small-sample artefact.',
  },
  {
    value: 'COMPOSITE',
    label: 'Composite (ensemble)',
    hint: 'Runs the selected engines CONCURRENTLY over the same bars, each with its own state '
      + 'machine, and merges their ledgers. Positions OVERLAP by design, and every merged trade '
      + 'is tagged with the engine that took it. NOT YET MEASURED on this data as an ensemble -- '
      + 'and read its net figures with care even once it is: the account prices a ledger '
      + 'sequentially, which is exact for one engine and is not a portfolio model once two are '
      + 'open at once, so it understates deployed capital. The run says so in a warning. Gross '
      + 'statistics are unaffected.',
  },
];

/**
 * PTA by default, as of 2026-10-03: the system under visual audit.
 *
 * <p>It was TRENDPULSE, on the grounds that it was the only provider clearing its own costs. That
 * reason no longer decides the default -- the pure forward-return test found NO directional edge in
 * the band signals, so the question this view answers is not which provider earns most but whether
 * the FSM's entries and exits are geometrically sensible on a chart. PTA is what is being read.
 *
 * <p>ONE declaration. The dialog seeds its own provider state from this constant rather than a
 * literal, so the audited default and the live view cannot disagree about what opens.
 */
export const DEFAULT_SIGNAL_PROVIDER = 'PTA';

const money = (value, places = 2) =>
  value === null || value === undefined || Number.isNaN(value)
    ? '—'
    : value.toLocaleString('en-US', {
        minimumFractionDigits: places,
        maximumFractionDigits: places,
      });

const signed = (value) =>
  value === null || value === undefined || Number.isNaN(value)
    ? '—'
    : `${value >= 0 ? '+' : '−'}${money(Math.abs(value))}`;

/* ── the chart ───────────────────────────────────────────────────────────────────────────── */

/**
 * A candlestick chart that grows one bar at a time.
 *
 * <p>Exposes `push` and `reset` through a ref rather than taking a data prop. A data prop would
 * mean the parent held every bar in state and re-rendered on each one; the chart keeps its own
 * series and the parent keeps none.
 */
export const StreamingChart = React.forwardRef(({ height }, ref) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { color: '#0b0f14' },
        textColor: 'rgba(255,255,255,0.7)',
      },
      grid: {
        vertLines: { color: 'rgba(255,255,255,0.05)' },
        horzLines: { color: 'rgba(255,255,255,0.05)' },
      },
      rightPriceScale: { borderColor: 'rgba(255,255,255,0.15)' },
      timeScale: { borderColor: 'rgba(255,255,255,0.15)', timeVisible: false },
      crosshair: { mode: 0 },
    });
    const series = chart.addCandlestickSeries({
      upColor: '#4ade80',
      downColor: '#ff6b6b',
      borderUpColor: '#4ade80',
      borderDownColor: '#ff6b6b',
      wickUpColor: 'rgba(74,222,128,0.6)',
      wickDownColor: 'rgba(255,107,107,0.6)',
    });
    chartRef.current = chart;
    seriesRef.current = series;
    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  React.useImperativeHandle(ref, () => ({
    push(candle) {
      // `update` rather than `setData`: it appends or replaces the last bar in place. setData with
      // a growing array would re-upload the whole series every second.
      seriesRef.current?.update(candle);
    },
    reset() {
      seriesRef.current?.setData([]);
    },
    fit() {
      chartRef.current?.timeScale().fitContent();
    },
    markers(list) {
      seriesRef.current?.setMarkers(list);
    },
  }), []);

  return <Box ref={containerRef} sx={{ width: '100%', height, bgcolor: '#0b0f14' }} />;
});
StreamingChart.displayName = 'StreamingChart';
StreamingChart.propTypes = { height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) };

/* ── the HUD ─────────────────────────────────────────────────────────────────────────────── */

/**
 * The glass panel over the chart's left edge.
 *
 * <p>Auto-scrolls to the newest line, but ONLY while the viewer is already at the bottom. Yanking
 * the panel down while somebody is reading back through it is the behaviour every log viewer gets
 * wrong; `pinned` is what stops it.
 */
export const EngineHud = ({ feed, state, symbol }) => {
  const scrollRef = useRef(null);
  const pinnedRef = useRef(true);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // A few pixels of tolerance: sub-pixel layout means scrollTop rarely lands exactly on the end.
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinnedRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [feed]);

  return (
    <Paper
      elevation={0}
      data-testid="dynaman-hud"
      sx={{
        position: 'absolute',
        top: 20,
        left: 20,
        zIndex: 10,
        width: 300,
        maxHeight: '70%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(8, 12, 18, 0.72)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 2,
        color: 'rgba(255,255,255,0.9)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" sx={{ letterSpacing: 1.2, opacity: 0.7 }}>
            DYNAMAN ENGINE · {symbol}
          </Typography>
          <Chip
            size="small"
            label={state}
            sx={{
              height: 20,
              fontSize: 11,
              bgcolor: 'rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.9)',
            }}
          />
        </Stack>
      </Box>

      <Box
        ref={scrollRef}
        onScroll={onScroll}
        sx={{ overflowY: 'auto', px: 1.5, py: 1, flex: 1, fontSize: 12 }}
      >
        {feed.length === 0 && (
          <Typography variant="caption" sx={{ opacity: 0.5 }}>
            Idle. Run the simulation to watch the engine evaluate each bar.
          </Typography>
        )}
        {feed.map((entry) => (
          <Box key={entry.id} sx={{ mb: 0.6 }}>
            <Typography
              component="div"
              sx={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 11.5,
                lineHeight: 1.35,
                color: FEED_COLORS[entry.level],
              }}
            >
              {entry.text}
            </Typography>
            {entry.detail && (
              <Typography
                component="div"
                sx={{ fontSize: 10, opacity: 0.4, fontFamily: 'ui-monospace, monospace' }}
              >
                {entry.detail}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};
EngineHud.propTypes = {
  feed: PropTypes.array.isRequired,
  state: PropTypes.string,
  symbol: PropTypes.string,
};
EngineHud.defaultProps = { state: 'CASH', symbol: '' };

/* ── the account strip ───────────────────────────────────────────────────────────────────── */

/** One figure, labelled. `dim` is for a mark, which must never read like a realised number. */
const Metric = ({ label, value, color, dim }) => (
  <Box sx={{ minWidth: 120 }}>
    <Typography variant="caption" sx={{ opacity: 0.55, display: 'block', lineHeight: 1.2 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: color || 'inherit', fontStyle: dim ? 'italic' : 'normal' }}
    >
      {value}
    </Typography>
  </Box>
);
Metric.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  color: PropTypes.string,
  dim: PropTypes.bool,
};

export const AccountStrip = ({ account, configured }) => {
  if (!account) {
    // Two different absences. Before the first bar there is no account STATE yet but the run is
    // still going to be sized — saying "unsized" there told a viewer the opposite of the truth,
    // which is the first thing they read.
    return (
      <Typography variant="caption" sx={{ opacity: 0.6 }}>
        {configured
          ? `${money(configured.startingEquity, 0)} account · `
            + `${configured.riskPerTradePct}% risk · ${configured.slippageBps} bps · `
            + `${configured.commissionPerShare}/share — awaiting the first bar`
          : 'Unsized run — percentage model, no costs.'}
      </Typography>
    );
  }
  const up = account.netPnl >= 0;
  return (
    <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
      <Metric label="Current equity" value={money(account.equity)} />
      <Metric
        label="Net PnL (realised)"
        value={`${signed(account.netPnl)} (${up ? '+' : ''}${account.netPnlPercent.toFixed(2)}%)`}
        color={up ? '#4ade80' : '#ff6b6b'}
      />
      <Metric
        label="Open notional"
        value={
          account.openShares > 0
            ? `${money(account.openNotional)} · ${account.openShares} sh`
            : 'Flat'
        }
      />
      {account.openShares > 0 && (
        <Metric
          label="Unrealised"
          value={signed(account.unrealisedPnl)}
          color={account.unrealisedPnl >= 0 ? '#4ade80' : '#ff6b6b'}
          dim
        />
      )}
      <Metric label="Closed trades" value={account.closedTrades} />
    </Stack>
  );
};
AccountStrip.propTypes = { account: PropTypes.object, configured: PropTypes.object };

/* ── the simulator ───────────────────────────────────────────────────────────────────────── */

const DynaManLiveSimulator = ({ symbol, chartHeight, account, initialProvider, customSeries }) => {
  const { token } = useAuth();
  const chartRef = useRef(null);
  const abortRef = useRef(null);

  const [feed, setFeed] = useState([]);
  const [state, setState] = useState('CASH');
  const [accountState, setAccountState] = useState(null);
  const [progress, setProgress] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [delayMs, setDelayMs] = useState(1000);
  // Seeded from the host so the live view opens on the provider the dialog is already running.
  // Two features one click apart showing different providers is the same defect class as them
  // running different exits, which this file's own history records.
  const [signalProvider, setSignalProvider] =
    useState(initialProvider || DEFAULT_SIGNAL_PROVIDER);
  const [report, setReport] = useState(null);

  const options = useMemo(
    // `customSeries` is null on every ordinary run and streamSimulation switches to POST only when
    // it is present -- so this one key is what lets the stepped view replay an uploaded file. In
    // the dependency list for the same reason the chart effect needs it: without it a series
    // parsed after this dialog mounted would never reach the request.
    () => ({ symbol, delayMs, signalProvider, customSeries,
      ...DEMO_STRATEGY, ...(account || DEMO_ACCOUNT) }),
    [symbol, delayMs, signalProvider, account, customSeries]
  );

  useEffect(() => () => abortRef.current?.abort(), []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setRunning(false);
  }, []);

  const run = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    chartRef.current?.reset();
    setFeed([]);
    setAccountState(null);
    setProgress(null);
    setReport(null);
    setError(null);
    setState('CASH');
    setRunning(true);

    try {
      await streamSimulation(
        async () => token,
        options,
        {
          onStart: (payload) => setWarnings(payload.warnings || []),
          // The window arrives before the first bar. Progress is counted WITHIN it: the run
          // processes all 169,144 bars but reports a 2,000-bar slice, and a readout of
          // "41,200 / 169,144" against a replay that stops at 43,000 reads as a crash.
          onWindow: (w) => setProgress({ ...w, index: w.firstBar }),
          onBar: (bar) => {
            chartRef.current?.push(toCandle(bar));
            setState(bar.state);
            setProgress((prev) => ({ ...(prev || {}), index: bar.index }));
            if (bar.account) {
              setAccountState(bar.account);
            }
            setFeed((prev) => appendFeed(prev, barLine(bar)));
          },
          onIntent: (intent) => setFeed((prev) => appendFeed(prev, intentLine(intent))),
          onRejection: (r) => setFeed((prev) => appendFeed(prev, rejectionLine(r))),
          // The size rides on the transition itself. It cannot come from the last bar's account:
          // the engine announces a transition BEFORE the bar that caused it, so on an entry that
          // state is still the flat one and the line could never say what was bought.
          onTransition: (t) => setFeed((prev) => appendFeed(prev, transitionLine(t, t))),
          // Appended rather than replacing: a late warning from the engine build is additional
          // to whatever the data-reachability check already said.
          onWarning: (message) =>
            setWarnings((prev) => (message && !prev.includes(message) ? [...prev, message] : prev)),
          onComplete: (payload) => setReport(payload),
          onError: (message) => setError(message),
        },
        controller.signal
      );
    } catch (e) {
      if (e?.name !== 'AbortError') {
        setError(e?.message || 'The simulation stream failed');
      }
    } finally {
      setRunning(false);
      chartRef.current?.fit();
    }
  }, [options, token]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#0b0f14' }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}
      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          {warnings.join(' · ')}
        </Alert>
      )}

      <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <StreamingChart ref={chartRef} height={chartHeight} />
        <EngineHud feed={feed} state={state} symbol={symbol} />
      </Box>

      <Paper
        square
        elevation={0}
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: 'rgba(12,17,24,0.95)',
          color: 'rgba(255,255,255,0.9)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            color={running ? 'error' : 'success'}
            startIcon={running ? <StopIcon /> : <PlayArrowIcon />}
            onClick={running ? stop : run}
          >
            {running ? 'Stop' : 'Run Simulation'}
          </Button>

          <Box sx={{ minWidth: 170 }}>
            <Typography variant="caption" sx={{ opacity: 0.55, display: 'block' }}>
              Signal provider
            </Typography>
            <Select
              value={signalProvider}
              onChange={(e) => setSignalProvider(e.target.value)}
              size="small"
              fullWidth
              // Locked while streaming: the provider is fixed when the engine is built, so
              // changing it mid-run would leave the control claiming something the run is not.
              disabled={running}
              inputProps={{ 'aria-label': 'Signal provider' }}
              sx={{
                color: 'rgba(255,255,255,0.9)',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.23)' },
                '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
              }}
            >
              {SIGNAL_PROVIDERS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ width: 180 }}>
            <Typography variant="caption" sx={{ opacity: 0.55 }}>
              Bar delay
            </Typography>
            <Slider
              size="small"
              value={delayMs}
              onChange={(_, v) => setDelayMs(v)}
              // Stepped to the three marks rather than continuous: the delay is a viewing speed,
              // and a slider that lands on 1,347 ms invites reading it as a tuning parameter.
              step={null}
              marks={DELAY_MARKS}
              min={500}
              max={2000}
              disabled={running}
              valueLabelDisplay="off"
              aria-label="Bar delay"
            />
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

          <AccountStrip account={accountState} configured={account || DEMO_ACCOUNT} />

          <Box sx={{ flex: 1 }} />

          {running && <CircularProgress size={18} />}
          {progress?.lastBar ? (
            <Tooltip
              title={`The engine is fed all ${progress.totalBars.toLocaleString()} bars; this `
                + `window is the stretch containing the run's decisions`}
            >
              <Typography variant="caption" sx={{ opacity: 0.55 }}>
                bar {(progress.index - progress.firstBar + 1).toLocaleString()} /{' '}
                {(progress.lastBar - progress.firstBar + 1).toLocaleString()}
                {progress.fromTime ? ` · ${progress.fromTime.slice(0, 10)} → `
                  + `${progress.toTime.slice(0, 10)}` : ''}
              </Typography>
            </Tooltip>
          ) : null}
        </Stack>
      </Paper>

      <FinalReportDialog report={report} symbol={symbol} onClose={() => setReport(null)} />
    </Box>
  );
};

DynaManLiveSimulator.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  customSeries: PropTypes.object,
  symbol: PropTypes.string,
  chartHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Overrides {@link DEMO_ACCOUNT}. Pass null for an unsized, percentage-only run. */
  account: PropTypes.object,
  /** Which provider to open on. Falls back to {@link DEFAULT_SIGNAL_PROVIDER}. */
  initialProvider: PropTypes.string,
};
DynaManLiveSimulator.defaultProps = {
  symbol: 'SPY',
  chartHeight: '100%',
  account: undefined,
  initialProvider: undefined,
};

/* ── the closing report ──────────────────────────────────────────────────────────────────── */

/**
 * The friction-adjusted summary, shown once the run ends.
 *
 * <p>Both halves are shown — gross and net — because the difference is the point of the account
 * layer. A modal showing only the net figure would let a viewer assume costs were negligible,
 * which on this strategy they are not: they are a fifth of the gross result.
 */
export const FinalReportDialog = ({ report, symbol, onClose }) => {
  const net = report?.netStatistics;
  return (
    <Dialog open={Boolean(report)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Simulation complete — {symbol}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {report && (
          <>
            <Typography variant="overline" sx={{ opacity: 0.6 }}>
              Gross — the rules, unsized and before costs
            </Typography>
            {/* GROSS, so no initialCapital: the compounded percentage curve is the figure
                this block is labelled with, and passing an account here would quietly turn it
                into the sized one. The net block below is where dollars belong. */}
            <DynaManStatsPanel trades={report.completedTrades} />

            {net ? (
              <Box sx={{ mt: 3 }}>
                <Typography variant="overline" sx={{ opacity: 0.6 }}>
                  Net — sized, after commission and slippage
                </Typography>
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Metric label="Starting equity" value={money(net.startingEquity)} />
                  <Metric label="Ending equity" value={money(net.endingEquity)} />
                  <Metric
                    label="Net profit"
                    value={`${signed(net.netProfit)} (${net.totalReturnPercent >= 0 ? '+' : ''}${net.totalReturnPercent.toFixed(2)}%)`}
                    color={net.netProfit >= 0 ? '#2e7d32' : '#c62828'}
                  />
                  <Metric
                    label="Net profit factor"
                    value={net.netProfitFactor == null ? 'n/a' : net.netProfitFactor.toFixed(2)}
                  />
                  <Metric label="Commissions" value={money(net.totalCommission)} />
                  <Metric label="Slippage" value={money(net.totalSlippage)} />
                  <Metric
                    label="Total friction"
                    value={`${money(net.totalFriction)}${
                      net.frictionShareOfGrossPercent == null
                        ? ''
                        : ` (${net.frictionShareOfGrossPercent.toFixed(1)}% of gross)`
                    }`}
                  />
                  <Metric label="Max drawdown" value={`${net.maxDrawdownPercent.toFixed(2)}%`} />
                  <Metric
                    label="Worst single loss"
                    value={`${net.worstLossPercent.toFixed(2)}% (budget ${net.riskBudgetPercent.toFixed(2)}%)`}
                  />
                  <Metric
                    label="Mean exposure"
                    value={`${net.averageExposurePercent.toFixed(1)}%`}
                  />
                  {net.unfundedCount > 0 && (
                    <Metric
                      label="Unfunded signals"
                      value={net.unfundedCount}
                      color="#ed6c02"
                    />
                  )}
                </Stack>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                This run was unsized, so the figures above are gross: no commission, no slippage and
                the full notional on every trade.
              </Alert>
            )}

            <Box sx={{ mt: 3 }}>
              <DynaManLedgerTable
                completedTrades={report.completedTrades || []}
                openTrade={report.openTrade}
              />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
FinalReportDialog.propTypes = {
  report: PropTypes.object,
  symbol: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default DynaManLiveSimulator;

/**
 * The simulator as a full-screen surface.
 *
 * <p>Full screen rather than a large dialog because the brief's first requirement is that the
 * chart takes the majority of the space, and the HUD is positioned against the chart's own edge —
 * in a boxed dialog the panel would sit over whichever bars happened to be at the left margin of a
 * much narrower viewport, which is a different and much worse picture.
 */
export const DynaManLiveSimulatorDialog = ({ open, symbol, onClose, initialProvider, customSeries }) => (
  <Dialog open={open} onClose={onClose} fullScreen>
    <DialogTitle
      sx={{
        pr: 6,
        py: 1,
        bgcolor: '#0b0f14',
        color: 'rgba(255,255,255,0.9)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        DynaMan — live engine replay
      </Typography>
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 6, color: 'rgba(255,255,255,0.7)' }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ p: 0, bgcolor: '#0b0f14', overflow: 'hidden' }}>
      {/* Mounted only while open, so the chart is created against a laid-out container and the
          stream is aborted on close by the simulator's own unmount cleanup. */}
      {open && <DynaManLiveSimulator symbol={symbol} initialProvider={initialProvider} 
      customSeries={customSeries}/>}
    </DialogContent>
  </Dialog>
);
DynaManLiveSimulatorDialog.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  customSeries: PropTypes.object,
  open: PropTypes.bool.isRequired,
  symbol: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  initialProvider: PropTypes.string,
};
